<template>
  <v-container fluid class="background-container">
    <!-- Main content row: Left column (form controls) + Right column (table) -->
    <v-row class="mt-0 flex-nowrap">
      <!-- Left column: Form controls (1/5 width) -->
      <v-col class="pb-0" cols="auto" style="width: 340px; padding-left: 30px">
        <!-- Search criteria card -->
        <v-card outlined class="subtle-white">
          <v-card-title
            class="text-h6 py-3 d-flex justify-space-between align-center"
          >
            <span>Επιλογή Βιβλίου</span>
            <div class="d-flex align-center">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on">
                    <v-btn
                      icon
                      small
                      @click="openBookDialog"
                      :disabled="!hasSearchedBook"
                      color="primary"
                    >
                      <v-icon>mdi-information-outline</v-icon>
                    </v-btn>
                  </span>
                </template>
                <span>{{ bookInfoTooltipText }}</span>
              </v-tooltip>

              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on">
                    <v-btn
                      icon
                      small
                      @click="downloadCsv"
                      :disabled="!hasSearchedBook"
                      color="success"
                      class="ml-1"
                    >
                      <v-icon>mdi-download</v-icon>
                    </v-btn>
                  </span>
                </template>
                <span>{{ csvDownloadTooltipText }}</span>
              </v-tooltip>
            </div>
          </v-card-title>
          <v-divider />
          <v-card-text>
            <v-form v-model="validForm" ref="searchForm">
              <!-- Search type selection -->
              <v-select
                :items="searchTypeOptions"
                outlined
                dense
                v-model="searchType"
                item-value="value"
                item-text="label"
                label="Τύπος Αναζήτησης"
                class="mb-0"
                @change="resetSearchResults"
              />

              <!-- Book ID / ISBN field -->
              <v-text-field
                :label="searchType === 'isbn' ? 'ISBN' : 'Κωδικός Βιβλίου'"
                outlined
                v-model.trim="searchValue"
                hide-spin-buttons
                :rules="searchValueRules"
                dense
                class="mb-0"
              />

              <!-- Academic Year select -->
              <v-select
                :items="$store.getters.yearsInfo.yearsList"
                outlined
                dense
                :rules="[utils.requiredRule]"
                v-model="academicYear"
                item-value="year"
                item-text="yearLabel"
                label="Ακαδ. Έτος"
                class="mb-0"
              />

              <!-- Search button -->
              <v-btn
                @click="handleSearch"
                :disabled="!validForm"
                block
                color="primary"
                class="mt-0"
              >
                Αναζήτηση
              </v-btn>
            </v-form>
          </v-card-text>
        </v-card>

        <!-- Book details card -->
        <v-card outlined class="subtle-white">
          <v-divider />
          <v-card-text>
            <!-- Title field -->
            <v-text-field
              label="Τίτλος"
              outlined
              v-model="title"
              dense
              class="mb-0"
            />

            <!-- Type field -->
            <v-text-field
              label="Τύπος Συγγράματος"
              outlined
              v-model="type"
              readonly
              dense
              class="mb-0"
            />

            <!-- ISBN field -->
            <v-text-field
              label="ISBN"
              outlined
              v-model="isbn"
              readonly
              dense
              class="mb-0"
            />

            <!-- Authors field -->
            <v-text-field
              label="Συγγραφείς"
              outlined
              v-model="authors"
              dense
              class="mb-0"
            />

            <!-- Publisher field -->
            <v-text-field
              label="Εκδότης"
              outlined
              v-model="publisherName"
              dense
              class="mb-0"
            />

            <!-- Book navigation - only shows when multiple books exist -->
            <v-row
              v-if="hasMultipleBooks"
              class="mb-0"
              justify="center"
              align="center"
            >
              <v-col cols="auto">
                <v-btn
                  icon
                  small
                  :disabled="currentBookIndex === 0"
                  @click="navigateBook(-1)"
                  color="primary"
                >
                  <v-icon>mdi-chevron-left</v-icon>
                </v-btn>
              </v-col>
              <v-col cols="auto" class="px-2">
                <span class="text-subtitle-2"
                  >{{ currentBookIndex + 1 }} / {{ totalBooks }}</span
                >
              </v-col>
              <v-col cols="auto">
                <v-btn
                  icon
                  small
                  :disabled="currentBookIndex === totalBooks - 1"
                  @click="navigateBook(1)"
                  color="primary"
                >
                  <v-icon>mdi-chevron-right</v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col
        class="flex-grow-1"
        style="padding-right: 30px; padding-left: 30px"
      >
        <v-card outlined class="subtle-white">
          <v-card-title class="text-h6 py-3 d-flex align-center">
            <v-spacer></v-spacer>
            <span>Ιδρύματα</span>
            <v-spacer></v-spacer>
            <v-tooltip bottom v-if="hasSearchedBook && totalResults > 0">
              <template v-slot:activator="{ on, attrs }">
                <v-chip
                  small
                  color="primary"
                  text-color="white"
                  v-bind="attrs"
                  v-on="on"
                >
                  {{ totalResults }}
                  {{ totalResults === 1 ? "αποτέλεσμα" : "αποτελέσματα" }}
                </v-chip>
              </template>
              <span>{{ resultsTooltipText }}</span>
            </v-tooltip>
          </v-card-title>
          <v-divider />
          <v-card-text v-if="showResultsCard" class="pa-0 subtle-white">
            <div v-if="expansionPanels.length === 0" class="text-center pa-6">
              <span class="text-subtitle-1">Δεν βρέθηκαν αποτελέσματα</span>
            </div>
            <v-expansion-panels v-else multiple>
              <v-expansion-panel
                v-for="(panel, index) in expansionPanels"
                :key="index"
              >
                <v-expansion-panel-header class="subtle-white">
                  <span class="text-subtitle-2"
                    >{{ panel.title }} ({{ panel.items.length }})</span
                  >
                </v-expansion-panel-header>
                <v-expansion-panel-content class="subtle-white">
                  <div v-if="panel.items.length === 0" class="text-center pa-4">
                    <span class="text-body-2">Δεν βρέθηκαν μαθήματα</span>
                  </div>
                  <v-data-table
                    v-else
                    :headers="tableHeaders"
                    :items="panel.items"
                    :items-per-page="-1"
                    hide-default-footer
                    class="elevation-0 subtle-white-2"
                  >
                    <template v-slot:item.professors="{ item }">
                      <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                          <span v-bind="attrs" v-on="on" class="professor-link">
                            {{ item.professors }}
                          </span>
                        </template>
                        <span>{{ item.professorsActual }}</span>
                      </v-tooltip>
                    </template>
                  </v-data-table>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>
<script>
import requests, { BASE_URL } from "@/requests";
import utils from "../utilities";

export default {
  data() {
    return {
      validForm: false,
      asyncRequests: 0,
      searchType: "isbn", // 'isbn' or 'eudoxus'
      searchTypeOptions: [
        { value: "isbn", label: "ISBN" },
        { value: "eudoxus", label: "Κωδικός Ευδόξου" },
      ],
      searchValue: "", // Used for both ISBN and bookId based on searchType
      academicYear: "",
      title: "",
      type: "",
      isbn: "",
      authors: "",
      publisherName: "",
      expansionPanels: [],
      tableHeaders: [
        { text: "ΙΔΡΥΜΑ", value: "institution", align: "center" },
        { text: "ΤΜΗΜΑ", value: "department", align: "center" },
        { text: "ΜΑΘΗΜΑ", value: "course", align: "center" },
        { text: "ΕΞΑΜΗΝΟ", value: "semester", align: "center" },
        { text: "ΠΕΡΙΟΔΟΣ", value: "period", align: "center" },
        { text: "", value: "professors", align: "center" },
      ],
      showResultsCard: false,

      // Book dialog
      hasSearchedBook: false,

      // Multi-book support
      books: [], // Array of all books from API
      allBookCourses: {}, // { bookId: { universityName: [...courses] } }
      currentBookIndex: 0, // Currently displayed book index
    };
  },

  computed: {
    utils() {
      return utils;
    },
    totalBooks() {
      return this.books.length;
    },
    hasMultipleBooks() {
      return this.books.length > 1;
    },
    currentBook() {
      return this.books[this.currentBookIndex] || null;
    },
    searchValueRules() {
      if (this.searchType === "isbn") {
        return [
          (v) => !!v || "Το πεδίο είναι υποχρεωτικό",
          (v) => {
            const cleaned = (v || "").replace(/[\s-]/g, "");
            return (
              /^([a-zA-Z0-9]{8}|[a-zA-Z0-9]{10}|[a-zA-Z0-9]{12}|[a-zA-Z0-9]{13})$/.test(
                cleaned
              ) || "Το ISBN πρέπει να έχει 8, 10, 12 ή 13 ψηφία"
            );
          },
        ];
      }
      return [utils.requiredRule];
    },
    bookInfoTooltipText() {
      return this.hasSearchedBook
        ? "Πατήστε για λεπτομέρειες βιβλίου"
        : "Αναζητήστε ένα βιβλίο και πατήστε για λεπτομέρειες";
    },
    csvDownloadTooltipText() {
      return this.hasSearchedBook
        ? "Κατεβάστε το αρχείο CSV"
        : "Αναζητήστε ένα βιβλίο και πατήστε για λήψη CSV";
    },
    totalResults() {
      return this.expansionPanels.reduce(
        (sum, panel) => sum + panel.items.length,
        0
      );
    },
    resultsTooltipText() {
      if (!this.currentBook) return "";
      const bookTitle = this.currentBook.title || "Άγνωστος τίτλος";
      if (this.hasMultipleBooks) {
        return `Αποτελέσματα για ${bookTitle}[${this.currentBookIndex + 1}]`;
      }
      return `Αποτελέσματα για ${bookTitle}`;
    },
  },

  watch: {
    asyncRequests: function (val) {
      if (val === 2) {
        this.$store.commit("setLoadingModal", false);
        this.asyncRequests = 0;
      }
    },
  },
  methods: {
    resetSearchResults() {
      this.showResultsCard = false;
      this.hasSearchedBook = false;
      this.expansionPanels = [];
      this.searchValue = "";
      this.academicYear = "";
      this.title = "";
      this.type = "";
      this.isbn = "";
      this.authors = "";
      this.publisherName = "";
      this.$store.commit("setSelectedBook", null);

      // Reset multi-book state
      this.books = [];
      this.allBookCourses = {};
      this.currentBookIndex = 0;

      // Reset form validation
      this.$nextTick(() => {
        if (this.$refs.searchForm) {
          this.$refs.searchForm.resetValidation();
        }
      });
    },

    openBookDialog() {
      this.$store.commit("setSelectedBook", this.currentBook);
      this.$store.commit("setBookInfoDialog", true);
    },

    // processIsbnForApi(isbn) {
    //   // Remove any whitespace
    //   const cleanIsbn = isbn.replace(/[\s-]/g, "");
	//
    //   // Transform based on length
    //   switch (cleanIsbn.length) {
    //     case 8:
    //       return cleanIsbn; // Use as is
    //     case 10:
    //       // Prepend 978 and keep first 12 digits
    //       // return ("978" + cleanIsbn).substring(0, 12);
	// 	  return (cleanIsbn).substring(0, 12);
    //     case 12:
    //       return cleanIsbn; // Use as is
    //     case 13:
    //       return cleanIsbn.substring(0, 12); // Remove last digit
    //     default:
    //       return null;
    //   }
    // },

    downloadCsv() {
      if (!this.currentBook || !this.academicYear) {
        return;
      }

      // Use book ID from current book object (not ISBN)
      const bookId = this.currentBook.id;
      const path = `rest/courses-books/book/eudoxus/courses/export-csv?bookId=${bookId}&year=${this.academicYear}`;

      // Construct full URL using BASE_URL (respects proxy in dev, production URL in prod)
      const fullUrl = BASE_URL + path;

      // Trigger download by navigating to the URL
      // The browser will handle the download thanks to Content-Disposition header
      window.location.href = fullUrl;
    },

    handleSearch() {
      // Dispatch to appropriate search method based on searchType
      if (this.searchType === "isbn") {
        this.handleSearchWithISBN();
      } else {
        this.handleSearchWithBookId();
      }
    },

    handleSearchWithBookId() {
      // Hide results card before new search
      this.showResultsCard = false;
      this.hasSearchedBook = false;

      this.$store.commit("setLoadingModal", true);
      requests.get(
        `rest/courses-books/book/eudoxus/info?bookId=${this.searchValue}`,
        this.searchCallbackBook
      );
      requests.get(
        `rest/courses-books/book/eudoxus/courses?bookId=${this.searchValue}&year=${this.academicYear}`,
        this.searchCallbackSecretariats
      );
    },

    handleSearchWithISBN() {
      // Hide results card before new search
      this.showResultsCard = false;
      this.hasSearchedBook = false;

      // const isbn = this.processIsbnForApi(this.searchValue);
		const isbn = this.searchValue;
      this.$store.commit("setLoadingModal", true);
      requests.get(
        `rest/courses-books/book/eudoxus/info?isbn=${isbn}`,
        this.searchCallbackBook
      );
      requests.get(
        `rest/courses-books/book/eudoxus/courses?isbn=${isbn}&year=${this.academicYear}`,
        this.searchCallbackSecretariats
      );
    },

    searchCallbackBook(r) {
      // Handle array response (new API) or single object (backwards compatible)
      this.books = Array.isArray(r) ? r : [r];
      this.currentBookIndex = 0;

      // Select and display first book
      if (this.books.length > 0) {
        this.selectBookAtIndex(0);
        this.hasSearchedBook = true;
      }

      this.asyncRequests++;
    },

    selectBookAtIndex(index) {
      if (index < 0 || index >= this.books.length) return;

      this.currentBookIndex = index;
      const book = this.books[index];

      // Update form fields
      this.title = book.title;
      console.log(book.type);
      this.type =
        book.type === "Published"
          ? "Έντυπο"
          : "Ηλεκτρονικό Σύγγραμμα/Σημειώσεις";
      this.isbn = book.isbn;
      this.authors = book.authors;
      this.publisherName = book.publisherName;

      // Update expansion panels for this book's courses
      this.updateExpansionPanelsForCurrentBook();
    },

    searchCallbackSecretariats(r) {
      // Store full nested response (keyed by book ID)
      this.allBookCourses = r;

      // Build expansion panels for current book
      this.updateExpansionPanelsForCurrentBook();

      // Show results card after data is ready
      this.showResultsCard = true;

      this.asyncRequests++;
    },

    updateExpansionPanelsForCurrentBook() {
      if (!this.currentBook || !this.allBookCourses) {
        this.expansionPanels = [];
        return;
      }

      const bookId = this.currentBook.id;
      const coursesForBook = this.allBookCourses[bookId] || {};

      this.expansionPanels = [];
      Object.keys(coursesForBook).forEach((institutionName) => {
        const departmentsCourses = coursesForBook[institutionName];
        const items = departmentsCourses.map((departmentsCourse) => ({
          institution: institutionName,
          department: departmentsCourse.departmentName,
          course:
            "[" +
            departmentsCourse.course.code +
            "] " +
            departmentsCourse.course.title,
          semester: departmentsCourse.course.semester,
          period: utils.getPeriodLabel(departmentsCourse.course.period),
          professors: "Διδάσκοντες",
          professorsActual:
            departmentsCourse.course.professor ||
            "Δεν υπάρχουν διαθέσιμοι διδάσκοντες",
        }));

        this.expansionPanels.push({
          title: institutionName,
          items: items,
        });
      });
    },

    navigateBook(direction) {
      const newIndex = this.currentBookIndex + direction;
      if (newIndex >= 0 && newIndex < this.books.length) {
        this.selectBookAtIndex(newIndex);
      }
    },
  },
};
</script>

<style scoped>
.professor-link {
  text-decoration: underline dotted; /* Dotted underline */
  text-underline-offset: 3px; /* Space between text and underline */
  cursor: help; /* Question mark cursor */
}
</style>
